<template>
  <UiComponentWrapper class="page">
    <OrganismSearchBlog
      :is-active="displaySearch"
      :minutes-read="data.componentGlobal.minRead"
      :posts="data.latestsFourthBlogPosts"
      @on:submit="search"
      @close:search="displaySearch = false"
    >
      {{ data.page.searchPlaceholderOpenResearch }}
    </OrganismSearchBlog>
    <TemplateBlogHeader
      :background-desktop="data.page.headerBackground"
      :background-mobile="data.page.headerBackgroundMobile"
      :categories="data.allCategories"
      :wording="data.page.headerAllCategories"
      @header:clicked="displaySearch = true"
    >
      <template #heading>
        {{ data.page.title }}
      </template>
      <template #search-placeholder>
        {{
          data.page.headerPlaceholderSearch
        }}
      </template>
      <template #search-text>
        {{ data.page.headerBrowseByCategories }}
      </template>
    </TemplateBlogHeader>
    <TemplateBlogPostsList
      :active-page="currentPage"
      :blog-posts="data.blogPostsInThisPage"
      :folder-name="folderName"
      :level="2"
      :minutes-read="data.componentGlobal.minRead"
      :number-of-pages="numberOfPages"
      :root="localePath('blog')"
    />
    <UiComponentContainer
      :start="2"
      :end="2"
      class="newsletter"
    >
      <MoleculeNewsletter
        as="paragraph"
        :icon="data.componentNewsletter.icon"
        @newsletter:subscribe="subscribeToTheNewsletter($event)"
      >
        <template #tag>
          {{ data.componentNewsletter.tag }}
        </template>
        <template #title>
          {{ data.componentNewsletter.title }}
        </template>
      </MoleculeNewsletter>
    </UiComponentContainer>
    <UiComponentContainer
      v-if="data.page.featuredGuide.length > 0"
      class="sur-footer"
    >
      <UiComponentHeading :level="2">
        {{ data.page.surFooterTitle }}
      </UiComponentHeading>
      <UiComponentWrapper>
        <AtomButton
          v-for="{ id, shortName, slug } in data.page.featuredGuide"
          :key="id"
          type="text"
          :to="
            localePath({
              name: 'resources-guides-slug',
              params: {
                slug: slug,
              },
            })
          "
        >
          {{ shortName }}
          <UiComponentIcon type="arrow-right" />
        </AtomButton>
      </UiComponentWrapper>
    </UiComponentContainer>
  </UiComponentWrapper>
</template>

<script setup>
// Layout
definePageMeta({
  layout: 'default',
})

// i18n
defineI18nRoute({
  paths: {
    de: '/blog/seite/[id]',
    en: '/blog/page/[id]',
    es: '/blog/pagina/[id]',
    fr: '/blog/page/[id]',
  },
})

// Composables
const { page, track } = useAnalyticsJS()
const { setAvailableLanguages } = useAvailableLanguages()
const { updateListItems } = useBreadcrumb()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const localePath = useLocalePath()
const { params } = useRoute()
const getRouteBaseName = useRouteBaseName()
const { push } = useRouter()
const { public: publicRuntimeConfig } = useRuntimeConfig()

// Async Data
const { data } = await useAsyncData(
  `page-blog-page-[id]-${params.id}-${locale.value}`,
  async () => {
    return Promise.all([
      $fetch(`/api/content/pages/blog-index/${locale.value}`, { query: { preview: state.preview } }),
      $fetch(`/api/content/pages/blog-posts/${locale.value}`, { query: { preview: state.preview } }),
    ])
  },
  {
    transform: ([{ allBlogPostCategories: allCategories, componentGlobal, componentNewsletter, home, page }, allBlogPosts]) => {
      const currentPage = Number.parseInt(params.id)
      const start = 12 + (27 * (currentPage - 2))
      const end = start + 27
      const blogPostsInThisPage = [...allBlogPosts]
        .slice(start, end)
        .map(
          ({
            id,
            blogPostCategory,
            coverImage,
            coverWithImgix,
            date,
            slug,
            subtitle,
            title,
            _updatedAt: updatedAt,
          }) => {
            return {
              id,
              coverImage: coverWithImgix || coverImage,
              date,
              description: subtitle,
              slug,
              tag: {
                link: blogPostCategory[0].slug,
                name: blogPostCategory[0].name,
              },
              title,
              updatedAt,
            }
          },
        )
      const latestsFourthBlogPosts = [...allBlogPosts]
        .slice(0, 4)
        .map(
          ({
            id,
            blogPostCategory,
            coverImage,
            coverWithImgix,
            date,
            slug,
            subtitle,
            title,
            _updatedAt: updatedAt,
          }) => {
            return {
              id,
              coverImage: coverWithImgix || coverImage,
              date,
              description: subtitle,
              slug,
              tag: {
                link: blogPostCategory[0].slug,
                name: blogPostCategory[0].name,
              },
              title,
              updatedAt,
            }
          },
        )

      return {
        allCategories,
        blogPostsInThisPage,
        componentGlobal,
        componentNewsletter,
        home,
        latestsFourthBlogPosts,
        page,
        numberOfArticles: allBlogPosts.length,
      }
    },
  },
)

// Meta
useSeoMeta({
  title: data.value.page.titleSeo,
  description: data.value.page.descriptionSeo,
  ogTitle: data.value.page.titleSeo,
  ogDescription: data.value.page.descriptionSeo,
  twitterTitle: data.value.page.titleSeo,
  twitterDescription: data.value.page.descriptionSeo,
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: `${publicRuntimeConfig.baseUrl}${localePath('blog')}`,
    },
  ],
  script: [
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': data.value.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': data.value.page.trackingBreadcrumbTitle || 'Blog',
            'item': `${publicRuntimeConfig.baseUrl}${localePath('blog')}`,
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': `Page ${params.id}`,
          },
        ],
      }),
      type: 'application/ld+json',
    },
  ],
})

// Refs
const currentPage = ref(Number.parseInt(params.id) || 2)
const displaySearch = ref(false)

// Computed Properties
const folderName = computed(() => {
  switch (locale.value) {
    case 'de':
      return 'seite'
    case 'es':
      return 'pagina'
    default:
      return 'page'
  }
})

const numberOfPages = computed(() => {
  const pageWithoutTheFirst = data.value.numberOfArticles - 12
  return Math.ceil(pageWithoutTheFirst / 27) + 1
})

// Methods
function search(filter) {
  push(
    localePath({ name: 'blog-search', query: { search: filter } }),
  )
}

function subscribeToTheNewsletter(email) {
  if (window.Didomi) window.Didomi.setUserAgreeToAll()
  track('Blog list joined', {
    category: 'Form',
    locale: locale.value,
    title: 'Blog',
    email,
  })
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  document.body.classList.remove('no-scroll')
})

onMounted(() => {
  page(`Blog - Page ${params.id}`)
  setAvailableLanguages([locale.value])
  updateListItems([
    {
      item: localePath('index'),
      name: data.value.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath('blog'),
      name: data.value.page.trackingBreadcrumbTitle || 'Blog',
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: `Page ${params.id}`,
    },
  ])
})
</script>

<style lang="postcss" scoped>
.page {
  @apply flex-col items-center;

  .container.newsletter {
    @apply mt-20;

    @screen md {
      @apply mt-34;
    }
  }

  .sur-footer {
    @apply mt-20 flex-col;

    @screen md {
      @apply mt-34;
    }

    .h2 {
      @apply font-medium text-2xl leading-8;

      @screen mdminus {
        font-size: 28px;
        line-height: 34px;
      }

      @screen mdplus {
        @apply text-3xl leading-10;
      }
    }

    .wrapper {
      @apply mt-8 flex flex-col items-start;

      @screen md {
        @apply flex-row flex-wrap justify-between;
      }

      .button {
        @apply mt-2 px-0 justify-start;
        height: 50px;

        @screen md {
          @apply w-85;
        }

        @screen xl {
          width: 290px;
        }
      }
    }
  }
}
</style>
